import React, { forwardRef } from 'react';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { FFTheme } from '../../assets/theme';

type OwnerState = {
  color: string;
  textTransform: string;
  verticalAlign: string;
  fontWeight: 'light' | 'regular' | 'medium' | 'bold';
  opacity: number;
  textGradient: boolean;
  darkMode?: boolean;
}

type MDTypographyRootProps = TypographyProps & {
  theme: FFTheme;
  ownerState: OwnerState;
  to?: string;
  href?: string;
}

const MDTypographyRoot = forwardRef<HTMLSpanElement, MDTypographyRootProps>(
  ({ theme, ownerState, children, ...rest }, ref) => {
    const { palette, typography, functions } = theme as any;
    const {
      color,
      textTransform,
      verticalAlign,
      fontWeight,
      opacity,
      textGradient,
      darkMode,
    } = ownerState;

    const { gradients, transparent, white } = palette;
    const {
      fontWeightLight,
      fontWeightRegular,
      fontWeightMedium,
      fontWeightBold,
    } = typography;
    const { linearGradient } = functions;

    const fontWeights = {
      light: fontWeightLight,
      regular: fontWeightRegular,
      medium: fontWeightMedium,
      bold: fontWeightBold,
    };

    const gradientStyles = () => ({
      backgroundImage:
        color !== 'inherit' &&
        color !== 'text' &&
        color !== 'white' &&
        gradients[color]
          ? linearGradient(gradients[color].main, gradients[color].state)
          : linearGradient(gradients.dark.main, gradients.dark.state),
      display: 'inline-block',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: transparent.main,
      position: 'relative',
      zIndex: 1,
    });

    let colorValue =
      color === 'inherit' || !palette[color] ? 'inherit' : palette[color].main;

    if (darkMode && (color === 'inherit' || !palette[color])) {
      colorValue = 'inherit';
    } else if (darkMode && color === 'dark') colorValue = white.main;

    const styles = {
      opacity,
      textTransform,
      verticalAlign,
      textDecoration: 'none',
      color: colorValue,
      fontWeight: fontWeights[fontWeight] && fontWeights[fontWeight],
      ...(textGradient && gradientStyles()),
      ...(rest.to && { cursor: 'pointer' }),
      ...(rest.href && { cursor: 'pointer' }),
    };

    return (
      <Typography style={styles as React.CSSProperties} ref={ref} {...rest}>
        {children}
      </Typography>
    );
  }
);

MDTypographyRoot.displayName = 'MDTypographyRoot';

export default MDTypographyRoot;
